"use client";

import { useSelector } from "@legendapp/state/react";
import { Upload } from "lucide-react";
import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { ScoreDisplay } from "./components/score-display";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResultsPanel } from "./results-panel";

import { setThumbnailReviewerState, STEP, thumbnailReviewerState, Results } from "./state";
import { trackEvent } from "@/utils/tracking";
// New ImagePopup component
function ImagePopup({
  isOpen,
  activeImage,
  onClose,
  onToggle,
  hasMultipleImages,
  altText,
}: {
  isOpen: boolean;
  activeImage: string | null;
  onClose: () => void;
  onToggle: () => void;
  hasMultipleImages: boolean;
  altText: string;
}) {
  if (!isOpen || !activeImage) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
      onClick={onClose}
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="relative flex h-full w-full items-center justify-center p-4">
        <img
          src={activeImage}
          alt={altText}
          className="max-h-[90vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Close button */}
        <button className="absolute right-4 top-4 text-white hover:text-gray-300" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Left/Right arrow buttons for navigation */}
        {hasMultipleImages && (
          <>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function UploadSection() {
  const { t } = useTranslation();
  const image = useSelector(thumbnailReviewerState.image);
  const improvedImage = useSelector(thumbnailReviewerState.improvedImage);
  const isLoading = useSelector(thumbnailReviewerState.isLoading);
  const videoDescription = useSelector(thumbnailReviewerState.videoDescription);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [activePopupImage, setActivePopupImage] = useState<string | null>(null);
  const step = useSelector(thumbnailReviewerState.step);
  const isUploading = isLoading && step === STEP.UPLOAD;
  const results = useSelector(thumbnailReviewerState.results);
  const improvedResults = useSelector(thumbnailReviewerState.improvedImageResults);
  const [isScoreDetailOpen, setIsScoreDetailOpen] = useState(false);
  const [activeResults, setActiveResults] = useState<Results | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");

  const processImageFile = async (file: File) => {
    setThumbnailReviewerState.isLoading(true);
    try {
      const uploadedImageUrl = URL.createObjectURL(file);
      setThumbnailReviewerState.image(uploadedImageUrl);
      setThumbnailReviewerState.step(STEP.DESCRIPTION);

      // await getImageDescription(file, t);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(t("thumbnailReviewer.upload.error"));
    } finally {
      setThumbnailReviewerState.isLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processImageFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const openImagePopup = (imageUrl: string) => {
    setActivePopupImage(imageUrl);
    setIsImagePopupOpen(true);
  };

  const closeImagePopup = () => {
    setIsImagePopupOpen(false);
    setActivePopupImage(null);
  };

  // New function to toggle between images in popup
  const togglePopupImage = () => {
    if (!image || !improvedImage) return;

    if (activePopupImage === image) {
      setActivePopupImage(improvedImage);
    } else {
      setActivePopupImage(image);
    }
  };

  // New effect to handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isImagePopupOpen) return;

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        togglePopupImage();
      } else if (e.key === "Escape") {
        closeImagePopup();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isImagePopupOpen, activePopupImage, image, improvedImage]);

  // New function to open score detail dialog
  const openScoreDetail = (resultsData: Results) => {
    setActiveResults(resultsData);
    setIsScoreDetailOpen(true);
  };

  // Add new function to extract YouTube video ID from URL
  const extractYoutubeId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Add function to fetch YouTube video title
  const fetchYoutubeVideoTitle = async (videoId: string): Promise<string | null> => {
    try {
      trackEvent("thumbnail_reviewer_fetch_youtube_video_title");
      // Using YouTube oEmbed API to get video title without requiring API key
      const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
      const titleResponse = await fetch(oEmbedUrl);

      if (titleResponse.ok) {
        const data = await titleResponse.json();
        return data.title || null;
      }
      return null;
    } catch (error) {
      console.error("Error fetching video title:", error);
      return null;
    }
  };

  // Add new function to handle YouTube URL submission
  const handleYoutubeUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!youtubeUrl.trim()) {
      toast.error(t("thumbnailReviewer.upload.emptyUrl"));
      return;
    }

    const videoId = extractYoutubeId(youtubeUrl);

    if (!videoId) {
      toast.error(t("thumbnailReviewer.upload.invalidUrl"));
      return;
    }

    setThumbnailReviewerState.isLoading(true);

    try {
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

      // Fetch the image to verify it exists and to convert to File
      const response = await fetch(thumbnailUrl);

      if (!response.ok) {
        throw new Error("Failed to fetch thumbnail");
      }

      const blob = await response.blob();
      const file = new File([blob], "youtube-thumbnail.jpg", { type: "image/jpeg" });

      // Set the image URL
      const uploadedImageUrl = URL.createObjectURL(file);
      setThumbnailReviewerState.image(uploadedImageUrl);
      setThumbnailReviewerState.step(STEP.DESCRIPTION);

      // Fetch and set video title
      const videoTitle = await fetchYoutubeVideoTitle(videoId);
      if (videoTitle) {
        setThumbnailReviewerState.videoDescription(videoTitle);
      }

      // Process the image
      // await getImageDescription(file, t);

      // Clear the input
      setYoutubeUrl("");
    } catch (error) {
      console.error("Error fetching YouTube thumbnail:", error);
      toast.error(t("thumbnailReviewer.upload.thumbnailError"));
    } finally {
      setThumbnailReviewerState.isLoading(false);
    }
  };

  return (
    <div className="">
      {/* Image Display Section - Always at top */}
      <div className="flex gap-4">
        <div className="flex flex-1 flex-col">
          <div
            className={`aspect-video ${!image ? "border-2 border-dashed" : ""} overflow-hidden rounded-lg`}
          >
            {!image ? (
              <div
                className="flex h-full flex-col items-center justify-center p-6 text-center"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {isUploading ? (
                  <div className="space-y-4">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-primary"></div>
                    <p>{t("thumbnailReviewer.upload.uploading")}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                      <Upload className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="mb-2 font-medium">{t("thumbnailReviewer.upload.dragDrop")}</h3>
                    <p className="mb-4 text-sm text-gray-500">
                      {t("thumbnailReviewer.upload.or")} {t("thumbnailReviewer.upload.browse")}
                    </p>
                    <input
                      type="file"
                      id="thumbnail-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleUpload}
                    />
                    <Button onClick={() => document.getElementById("thumbnail-upload")?.click()}>
                      {t("thumbnailReviewer.upload.selectImage")}
                    </Button>
                    <div className="mt-6">
                      <form
                        onSubmit={handleYoutubeUrlSubmit}
                        className="flex w-full flex-col items-center"
                      >
                        <div className="mb-2 px-12 text-center text-sm font-medium text-orange-500">
                          {t("thumbnailReviewer.upload.orYoutubeUrl")}
                        </div>
                        <div className="flex w-full">
                          <input
                            type="text"
                            value={youtubeUrl}
                            onChange={(e) => setYoutubeUrl(e.target.value)}
                            placeholder={t("thumbnailReviewer.upload.youtubeUrlPlaceholder")}
                            className="h-[42px] flex-1 rounded-l-lg border border-gray-300 p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            disabled={isLoading}
                          />
                          <Button
                            type="submit"
                            className="h-[42px] rounded-l-none"
                            disabled={isLoading}
                          >
                            {isLoading
                              ? t("thumbnailReviewer.upload.loading")
                              : t("thumbnailReviewer.upload.fetch")}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="relative h-full w-full bg-gray-100">
                <img
                  src={image}
                  alt={t("thumbnailReviewer.description.thumbnailPreview")}
                  className="h-full w-full cursor-pointer object-contain"
                  onClick={() => openImagePopup(image)}
                />
              </div>
            )}
          </div>
          {/* Score display for original image */}
          {image && results && step === STEP.IMPROVEMENTS && (
            <ScoreDisplay
              score={results.overallScore}
              label={t("thumbnailReviewer.results.overallScore")}
              className="mt-4 cursor-pointer rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
              onClick={() => openScoreDetail(results)}
            />
          )}
        </div>

        {/* Show improved image if available */}
        {image && improvedImage && (
          <div className="flex flex-1 flex-col">
            <div className="aspect-video overflow-hidden rounded-lg">
              <div className="relative h-full w-full bg-gray-100">
                <img
                  src={improvedImage}
                  alt={t("thumbnailReviewer.description.improvedPreview")}
                  className="h-full w-full cursor-pointer object-contain"
                  onClick={() => openImagePopup(improvedImage)}
                />
                <div className="absolute left-2 top-2 rounded bg-red-500 px-2 py-1 text-sm text-white">
                  {t("thumbnailReviewer.description.improved")}
                </div>
              </div>
            </div>
            {/* Score display for improved image */}
            {improvedResults && (
              <ScoreDisplay
                score={improvedResults.overallScore}
                label={t("thumbnailReviewer.results.overallScore")}
                className="mt-4 cursor-pointer rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
                onClick={() => openScoreDetail(improvedResults)}
              />
            )}
          </div>
        )}
      </div>

      {/* Video Description Textarea */}
      <div className="mt-4">
        <textarea
          className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          rows={3}
          placeholder={t("thumbnailReviewer.description.placeholder")}
          value={videoDescription}
          onChange={(e) => setThumbnailReviewerState.videoDescription(e.target.value)}
          disabled={isLoading || (step !== STEP.UPLOAD && step !== STEP.DESCRIPTION)}
        />
        <p className="mt-1 text-sm text-gray-500">{t("thumbnailReviewer.description.helpText")}</p>
      </div>

      {/* Score Detail Dialog */}
      <Dialog open={isScoreDetailOpen} onOpenChange={setIsScoreDetailOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t("thumbnailReviewer.results.detailedAnalysis")}</DialogTitle>
          </DialogHeader>
          {activeResults && <ResultsPanel results={activeResults} isInPopup={true} />}
        </DialogContent>
      </Dialog>

      {/* Replace the image popup with the new component */}
      <ImagePopup
        isOpen={isImagePopupOpen}
        activeImage={activePopupImage}
        onClose={closeImagePopup}
        onToggle={togglePopupImage}
        hasMultipleImages={!!image && !!improvedImage}
        altText={t("thumbnailReviewer.description.thumbnailPreview")}
      />
    </div>
  );
}
